import React, { useState, useContext, useEffect } from 'react';
import {
    Grid,
    Table,
    TableHeaderRow,
    TableFilterRow,
    TableColumnVisibility,
    PagingPanel,
} from '@devexpress/dx-react-grid-bootstrap4';

import { SelectionState, FilteringState, PagingState, CustomPaging, SortingState } from '@devexpress/dx-react-grid';
import { ChangeTablePage, SortTable } from '../tables/shared/TableEventHelpers';
import { DataContext } from '../../stores/teacher-table-store';
import { GetTeachers } from './requests/TeacherRequests';
import ExlCellComponent from './ExlCellComponent';
import Loading from '../Loading';
import TableRow from '../tables/shared/TableRow';
import TableHeader from '../tables/shared/TableHeader';
import ColumnChooser from '../tables/shared/ColumnChooser';
import TableFooter from '../tables/shared/TableFooter';
import FilterResetter from '../tables/shared/FilterResetter';
import NumberOfSearchResults from '../tables/shared/NumberOfSearchResults';
import NoDataRow from '../tables/shared/NoDataRow';
import FilterCell from './TeacherFilterCell';
import { TeacherFilterOperations } from '../tables/shared/FilterOperations';
import { PagingShowAllText } from '../../functions/TableFunctions';
import { useTranslation } from 'react-i18next';
import { hideColumsDependingOnWindowSize } from './TeacherTableHelperFunctions';
import { ACTIVE, INACTIVE } from '../../i18n/keyConstants';

const TeacherTable = () => {
    const { store, dispatch } = useContext(DataContext);
    const [isLoading, setIsLoading] = useState(false);
    const error = null;
    const {
        columns,
        defaultFilters,
        hiddenColumns,
        pageSize,
        currentPage,
        tableSize,
        filters,
        tableColumnExtensions,
        teachers,
        direction,
        sortInfo,
        columnName,
    } = store;
    const { t } = useTranslation(['teacherTable', 'shared']);


    async function getTeachers() {
        setIsLoading(true);
        const {rows, totalRows } = await GetTeachers(pageSize, currentPage, direction, columnName, JSON.stringify(filters));
        dispatch({
            type: 'FETCH_SUCCESS',
            totalRows,
            teachers: rows,
        });
        handleRowFormatting(rows);
        setIsLoading(false);
        PagingShowAllText(t);
    }

    function translateColumnTitles() {
        columns.map((col) => {
            col.title = t(col.title);
        })
    }

    const handleRowFormatting = (teachers) => {
        teachers.forEach(function (teacher) {
            teacher.isActive = teacher.isActive === true ? t(ACTIVE, { ns: 'shared' }) : t(INACTIVE, {ns: 'shared'});
            teacher.numCourses = teacher.courses.length;
        });
    }

    const handlePageSizeChange = (pageSize) => {
        ChangeTablePage(pageSize, dispatch, currentPage, tableSize);
    };

    const handleSortChange = (sorting) => {
        let shouldSort = true;

        if (shouldSort) {
            SortTable(sorting, sortInfo, dispatch);
        }
    };
    
    useEffect(() => {
        getTeachers();
        translateColumnTitles();
    }, [currentPage, filters, pageSize, direction, columnName]);


    useEffect(() => {
        window.addEventListener('resize', (() => hideColumsDependingOnWindowSize(dispatch)));
        hideColumsDependingOnWindowSize(dispatch);
        return () => {
            window.removeEventListener('resize', (() => hideColumsDependingOnWindowSize(dispatch)));
        }
    }, []);


    return (
        <Grid columns={columns} rows={teachers} className='padding-right-60px'>
            <PagingState
                pageSize={pageSize}
                currentPage={currentPage - 1}
                onPageSizeChange={handlePageSizeChange}
                onCurrentPageChange={(currentPage) => {
                    dispatch({ type: 'CURRENT_PAGE', payload: currentPage + 1 });
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }}
            />
            <SortingState onSortingChange={handleSortChange} defaultSorting={[{ columnName, direction }]} />
            <CustomPaging totalCount={tableSize} />
            <SelectionState />
            <FilteringState defaultFilters={defaultFilters} />
            <Table
                columnExtensions={tableColumnExtensions }
                cellComponent={ExlCellComponent}
                rowComponent={(props) => TableRow(props, error, 'teacher')}
                noDataRowComponent={(props) => NoDataRow(isLoading, error, props)} />
            <TeacherFilterOperations />
            <TableFilterRow showFilterSelector cellComponent={FilterCell} />
            <TableHeaderRow showSortingControls />
            <PagingPanel pageSizes={[10, 25, 50, 10000]} messages={PagingShowAllText} />
            <TableColumnVisibility hiddenColumnNames={hiddenColumns} />
            <TableHeader tableSize={tableSize}>
                <ColumnChooser
                    hiddenColumns={hiddenColumns}
                    columns={columns}
                    onHiddenColumnChange={(hiddenColumns) => {
                        dispatch({ type: 'HIDDEN_COLUMNS_AND_CUSTOM_COLS', hiddenColumns, showCustomCols: true });
                    }}
                    onReset={() => {
                        dispatch({ type: 'RESET_HIDDEN_COLUMNS' });
                    }}
                />
                <FilterResetter DataContext={DataContext} />
            </TableHeader>
            {isLoading ? <Loading /> : null}
            <TableFooter usedInExl={true} >
                <NumberOfSearchResults pageSize={pageSize} tableSize={tableSize} currentPage={currentPage} t={t} />
            </TableFooter>
        </Grid>
    );
};

export default TeacherTable;

